/**
 * Enum for dynamic section data types
 */
export enum DynamicDataType {
    MaskedUInt16 = 'MaskedUInt16',
    UInt16 = 'UInt16',
    Float = 'Float',
    Float2Byte = 'Float2Byte',
}

/**
 * Enum for validation rule types
 */
export enum DynamicValidationRuleType {
    KeyValuePairs = 'KeyValuePairs',
    Enum = 'Enum',
    Range = 'Range',
    MinLength = 'MinLength',
    MaxLength = 'MaxLength',
    Precision = 'Precision',
    DisplayValueList = 'DisplayValueList',
}

export enum SensorInstallType {
    Contact = 0,
    NonContact = 1,
}

/**
 * Enum for flex settings
 */
export enum FlexSettings {
    UpDepthElectronicOffset = 'UpDepth electronic offset',
    PDepthElectronicOffset = 'Pdepth electronic offset',
    PeakVelocityGain = 'Peak/Gated velocity Gain',
    SurfaceVelocityGain = 'Surface velocity gain'
}

/**
 * Interface for the dynamic section identifier
 */
export interface DynamicSectionId {
    name: string;
    version: string;
    sensorType: string | number;
    customerId?: string | null;
}

/**
 * Interface for display groups
 */
export interface DisplayGroup {
    name: string;
    order: number;
    isToggleable: boolean;
}

/**
 * Interface for key-value pairs validation rule
 */
export interface KeyValuePairsValidationRule {
    ruleType: DynamicValidationRuleType.KeyValuePairs;
    values: Record<string, string[]>;
    advancedKeys?: string[];
}

/**
 * Interface for enum validation rule
 */
export interface EnumValidationRule {
    ruleType: DynamicValidationRuleType.Enum;
    values: string[];
    isAdvanced?: boolean;
}

/**
 * Interface for range validation rule
 */
export interface RangeValidationRule {
    ruleType: DynamicValidationRuleType.Range;
    minValue?: number;
    maxValue?: number;
}

/**
 * Interface for min length validation rule
 */
export interface MinLengthValidationRule {
    ruleType: DynamicValidationRuleType.MinLength;
    value: number;
}

export interface MaxLengthValidationRule {
    ruleType: DynamicValidationRuleType.MaxLength;
    value: number;
}

/**
 * Interface for precision validation rule
 */
export interface PrecisionValidationRule {
    ruleType: DynamicValidationRuleType.Precision;
    precision: number;
}

/**
 * Interface for display value list validation rule
 */
export interface DisplayValueListValidationRule {
    ruleType: DynamicValidationRuleType.DisplayValueList;
    values: Record<string, number | string | null>;
    modbusRegister?: number;
    displayUnitType?: string;
    displayUnitTitle?: string;
    registerUnitType?: string;
}

/**
 * Union type for all dynamic validation rule types
 */
export type DynamicValidationRule =
    | KeyValuePairsValidationRule
    | EnumValidationRule
    | RangeValidationRule
    | MinLengthValidationRule
    | MaxLengthValidationRule
    | PrecisionValidationRule
    | DisplayValueListValidationRule;

/**
 * Interface for setting configuration
 */
export interface DynamicSetting {
    name: string;
    description: string;
    defaultValue: string | number | Record<string, string> | null;
    displayGroupName: string;
    dataType: DynamicDataType;
    isAdvanced: boolean;
    unitType?: string;
    unitSuffix?: string;
    validationRules: DynamicValidationRule[];
}

/**
 * Interface for the complete dynamic section
 */
export interface DynamicSection {
    id: DynamicSectionId;
    installType?: SensorInstallType;
    allowNegativePhysicalOffset: boolean;
    displayGroups: DisplayGroup[];
    settings: DynamicSetting[];
    sourcesforDepth?: RegisterEntityType[];
    sourcesforVelocity?: RegisterEntityType[];
}

/**
 * Interface for sensor configuration
 */
export interface SensorConfiguration {
    id?: number | string;
    sensorName: string;
    sensorVersion: string;
    sensorType: string | number;
    slaveId: number;
    channel: number;
    monitoringPoint: number;
    physicalOffset: number;
    settings: Record<string, string | number | SensorEnablesSettings>;
}

/**
 * Helper interface for dropdown options
 */
export interface SelectOption {
    text: string;
    value: string;
}

export interface RegisterEntityTypeOption {
    text: string;
    value: RegisterEntityType;
}

export interface AvailableDynamicSection {
    name: string;
    sensorType: number;
    version: string;
}

/**
 * Interface for sensor enables settings
 */
export interface SensorEnablesSettings {
    Measurement?: string;
    Scan?: string;
    Waveform?: string;
    'Store Data'?: string;
    'Store State Changes'?: string;
    'Store Raw '?: string;
    'Auto Calibration'?: string;
}

/**
 * Enum for register entity types used in monitoring point preferences
 */
export enum RegisterEntityType {
    Auto = 'Auto',
    Depth = 'Depth',
    Range = 'Range',
    UpDepth = 'UpDepth',
    SDepth = 'SDepth',
    ParaDepth = 'ParaDepth',
    LRDepth = 'LRDepth',
    PDepth = 'PDepth',
    PDepthUnadjusted = 'PDepthUnadjusted',
    PDepthSurcharge = 'PDepthSurcharge',
    PRESSK = 'PRESSK',
    Rain = 'Rain',
    RainIntensity = 'RainIntensity',
    Velocity = 'Velocity',
    PeakVelocity = 'PeakVelocity',
    GatedVelocity = 'GatedVelocity',
    SurfaceVelocity = 'SurfaceVelocity',
    SurchargeVelocity = 'SurchargeVelocity',
    Quantity = 'Quantity',
    FlowTotal = 'FlowTotal',
    FlowRate = 'FlowRate',
    Temperature = 'Temperature',
    WaterTemperature = 'WaterTemperature',
    AirTemperature = 'AirTemperature',
    WaterTemperature2 = 'WaterTemperature2',
    AirTemperature2 = 'AirTemperature2',
    AirTemperature3 = 'AirTemperature3',
    RainGaugeTemperature = 'RainGaugeTemperature',
    Counts = 'Counts',
    State = 'State',
    PDepthCounts = 'PDepthCounts',
    LiquidLevel = 'LiquidLevel',
    RainTips = 'RainTips',
    DIN1 = 'DIN1',
    DIN2 = 'DIN2',
    DIN3 = 'DIN3',
    DIN4 = 'DIN4',
    DIN5 = 'DIN5',
    DIN6 = 'DIN6',
    DIN7 = 'DIN7',
    DIN8 = 'DIN8',
    DIN9 = 'DIN9',
    DIN10 = 'DIN10',
    SystemAlarmStatus = 'SystemAlarmStatus',
    MP1AlarmStatus = 'MP1AlarmStatus',
    MP2AlarmStatus = 'MP2AlarmStatus',
    Volts = 'Volts',
    BatteryVolts = 'BatteryVolts',
    SupplyVolts = 'SupplyVolts',
    AIN1_volts = 'AIN1_volts',
    AIN2_volts = 'AIN2_volts',
    AIN3_volts = 'AIN3_volts',
    AIN4_volts = 'AIN4_volts',
    AIN5_volts = 'AIN5_volts',
    AIN6_volts = 'AIN6_volts',
    AIN7_volts = 'AIN7_volts',
    AIN8_volts = 'AIN8_volts',
    AIN9_volts = 'AIN9_volts',
    AIN10_volts = 'AIN10_volts',
    RSSI_dBm = 'RSSI_dBm',
    AIN1_ma = 'AIN1_ma',
    AIN2_ma = 'AIN2_ma',
    AIN3_ma = 'AIN3_ma',
    AIN4_ma = 'AIN4_ma',
    AIN5_ma = 'AIN5_ma',
    AIN6_ma = 'AIN6_ma',
    AIN7_ma = 'AIN7_ma',
    AIN8_ma = 'AIN8_ma',
    AIN9_ma = 'AIN9_ma',
    AIN10_ma = 'AIN10_ma',
    Capcitance1 = 'Capcitance1',
    Capcitance2 = 'Capcitance2',
    Capcitance3 = 'Capcitance3',
    Capcitance4 = 'Capcitance4',
    Angle = 'Angle',
    Inclinometer = 'Inclinometer',
    Angle_X = 'Angle_X',
    Angle_Y = 'Angle_Y',
    Angle_Z = 'Angle_Z',
    SensorSlope = 'SensorSlope',
    SensorRotation = 'SensorRotation',
}

export const RegisterEntityTypeToNumber: Record<RegisterEntityType, number> = {
    [RegisterEntityType.Auto]: 0,
    [RegisterEntityType.Depth]: 1,
    [RegisterEntityType.Range]: 2,
    [RegisterEntityType.UpDepth]: 3,
    [RegisterEntityType.SDepth]: 4,
    [RegisterEntityType.ParaDepth]: 5,
    [RegisterEntityType.LRDepth]: 6,
    [RegisterEntityType.PDepth]: 7,
    [RegisterEntityType.PDepthUnadjusted]: 8,
    [RegisterEntityType.PDepthSurcharge]: 9,
    [RegisterEntityType.PRESSK]: 10,
    [RegisterEntityType.Rain]: 11,
    [RegisterEntityType.RainIntensity]: 12,
    [RegisterEntityType.Velocity]: 256,
    [RegisterEntityType.PeakVelocity]: 257,
    [RegisterEntityType.GatedVelocity]: 258,
    [RegisterEntityType.SurfaceVelocity]: 259,
    [RegisterEntityType.SurchargeVelocity]: 260,
    [RegisterEntityType.Quantity]: 512,
    [RegisterEntityType.FlowTotal]: 513,
    [RegisterEntityType.FlowRate]: 528,
    [RegisterEntityType.Temperature]: 768,
    [RegisterEntityType.WaterTemperature]: 769,
    [RegisterEntityType.AirTemperature]: 770,
    [RegisterEntityType.WaterTemperature2]: 771,
    [RegisterEntityType.AirTemperature2]: 772,
    [RegisterEntityType.AirTemperature3]: 773,
    [RegisterEntityType.RainGaugeTemperature]: 774,
    [RegisterEntityType.Counts]: 1024,
    [RegisterEntityType.State]: 1025,
    [RegisterEntityType.PDepthCounts]: 1026,
    [RegisterEntityType.LiquidLevel]: 1027,
    [RegisterEntityType.RainTips]: 1028,
    [RegisterEntityType.DIN1]: 1029,
    [RegisterEntityType.DIN2]: 1030,
    [RegisterEntityType.DIN3]: 1031,
    [RegisterEntityType.DIN4]: 1032,
    [RegisterEntityType.DIN5]: 1033,
    [RegisterEntityType.DIN6]: 1034,
    [RegisterEntityType.DIN7]: 1035,
    [RegisterEntityType.DIN8]: 1036,
    [RegisterEntityType.DIN9]: 1037,
    [RegisterEntityType.DIN10]: 1038,
    [RegisterEntityType.SystemAlarmStatus]: 1104,
    [RegisterEntityType.MP1AlarmStatus]: 1105,
    [RegisterEntityType.MP2AlarmStatus]: 1106,
    [RegisterEntityType.Volts]: 1280,
    [RegisterEntityType.BatteryVolts]: 1281,
    [RegisterEntityType.SupplyVolts]: 1282,
    [RegisterEntityType.AIN1_volts]: 1296,
    [RegisterEntityType.AIN2_volts]: 1297,
    [RegisterEntityType.AIN3_volts]: 1298,
    [RegisterEntityType.AIN4_volts]: 1299,
    [RegisterEntityType.AIN5_volts]: 1300,
    [RegisterEntityType.AIN6_volts]: 1301,
    [RegisterEntityType.AIN7_volts]: 1302,
    [RegisterEntityType.AIN8_volts]: 1303,
    [RegisterEntityType.AIN9_volts]: 1304,
    [RegisterEntityType.AIN10_volts]: 1305,
    [RegisterEntityType.RSSI_dBm]: 1341,
    [RegisterEntityType.AIN1_ma]: 1536,
    [RegisterEntityType.AIN2_ma]: 1537,
    [RegisterEntityType.AIN3_ma]: 1538,
    [RegisterEntityType.AIN4_ma]: 1539,
    [RegisterEntityType.AIN5_ma]: 1540,
    [RegisterEntityType.AIN6_ma]: 1541,
    [RegisterEntityType.AIN7_ma]: 1542,
    [RegisterEntityType.AIN8_ma]: 1543,
    [RegisterEntityType.AIN9_ma]: 1544,
    [RegisterEntityType.AIN10_ma]: 1545,
    [RegisterEntityType.Capcitance1]: 1792,
    [RegisterEntityType.Capcitance2]: 1793,
    [RegisterEntityType.Capcitance3]: 1794,
    [RegisterEntityType.Capcitance4]: 1795,
    [RegisterEntityType.Angle]: 2048,
    [RegisterEntityType.Inclinometer]: 2049,
    [RegisterEntityType.Angle_X]: 2050,
    [RegisterEntityType.Angle_Y]: 2051,
    [RegisterEntityType.Angle_Z]: 2052,
    [RegisterEntityType.SensorSlope]: 2053,
    [RegisterEntityType.SensorRotation]: 2054,
};

/**
 * Interface for register entity source mapping (sensor data source configuration)
 */
export interface RegisterEntitySourceMapping {
    entityType?: RegisterEntityType;
    dataAddress?: number;
    qualityAddress?: number;
}

/**
 * Interface for monitoring point data source preferences
 */
export interface SensorMonitoringPointDataSource {
    depthSensorId?: number;
    depthEntityType?: RegisterEntityType;
    velocitySensorId?: number;
    velocityEntityType?: RegisterEntityType;
}

/**
 * Interface for sensor configuration collection with monitoring point preferences
 */
export interface SensorConfigurationCollection {
    mp_1?: SensorMonitoringPointDataSource;
    mp_2?: SensorMonitoringPointDataSource;
    sensors: SensorConfiguration[];
}

export interface SensorConfigurationCollectionRequest {
    mp_1: MonitoringPointDataSourceRequest | null;
    mp_2: MonitoringPointDataSourceRequest | null;
    sensors: SensorConfigurationRequest[];
}

export interface MonitoringPointDataSourceRequest {
    depthSensorId: number;
    depthEntityType: number;
    velocitySensorId?: number;
    velocityEntityType?: number;
}

export interface SensorConfigurationRequest {
    id: number;
    sensorName: string;
    sensorVersion: string;
    sensorType: number;
    slaveId: number;
    channel: number;
    monitoringPoint: number;
    physicalOffset: number;
    physicalOffsetUnit: number;
    physicalOffsetReference: number;
    metadata: SensorMetadata[];
    settings: Record<string, string | number | boolean | SensorEnablesSettings>;
}

export interface SensorMetadata {
    name: string;
    lastUpdatedUtc: string;
    version: number;
}
